import { MasterTranslation } from '@/constants';
import { useGlobalState } from '@/hooks';
import type { IShipmentData } from '@/modules/pmt/interfaces';
import { isEddUtc } from '@/modules/pmt/utils';
import dayjs from 'dayjs';
// https://day.js.org/docs/en/plugin/is-tomorrow
import isTomorrow from 'dayjs/plugin/isTomorrow';
import timezone from 'dayjs/plugin/timezone';
// https://day.js.org/docs/en/plugin/timezone
import utc from 'dayjs/plugin/utc';
import { EDDContainer, EDDIcon, EDDText } from './EDD.styles';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isTomorrow);

const EDD_DATE_FORMAT = 'DD MMM YY (ddd)';
const DAYS_TO_DISPLAY_DIFFERENCE = 7;

export enum Variant {
  Card = 'Card',
  Details = 'Details',
}

interface IEDDProps {
  shipmentData?: IShipmentData;
  variant?: Lowercase<Variant>;
}

const EDD = ({ shipmentData, variant = 'card' }: IEDDProps) => {
  const { start, end } = shipmentData?.edd?.display || {};
  const { t } = useGlobalState();

  if (!start && !end) return null;

  const eddStart = dayjs(start);
  const eddEnd = dayjs(end);
  // https://parcelperform.slack.com/archives/C05FAEL5EER/p1694074592073729?thread_ts=1693898859.077329&cid=C05FAEL5EER
  // show browser time if edd is utc and raw start/end if edd is not
  const formattedStart = isEddUtc(start) ? eddStart.format(EDD_DATE_FORMAT) : start;
  const formattedEnd = isEddUtc(end) ? eddEnd.format(EDD_DATE_FORMAT) : end;
  // dayjs without third parameter of diff, dayjs will TRUNCATE the result
  // so we need to use floating point result + ceil to get the correct result
  const dayDiffFromNow = Math.floor(eddStart.diff(new Date(), 'day', true));

  const delivery = t(MasterTranslation.Delivery);

  const getEddDisplay = (): string => {
    switch (true) {
      // Same day
      case eddStart.isToday():
        return `${delivery}: ${t(MasterTranslation.Today)}`;
      // Tomorrow
      case eddStart.isTomorrow():
        return t(MasterTranslation.DeliveryCountdownOne);
      // Within 7 days
      case dayDiffFromNow <= DAYS_TO_DISPLAY_DIFFERENCE:
        return `${t(MasterTranslation.DeliveryCountdown)}`.replace('{n}', `${dayDiffFromNow}`);
      // More than 7 days, no end date
      case !end:
        return `${delivery}: ${formattedStart}`;
      // More than 7 days, with end date
      case eddStart.isBefore(eddEnd):
        return `${delivery}: ${formattedStart} - ${formattedEnd}`;
      // Same day, with end date
      case eddStart.isSame(eddEnd):
        return `${delivery}: ${formattedStart}`;
      default:
        return '';
    }
  };

  return (
    <EDDContainer variant={variant}>
      <EDDIcon />
      <EDDText eddVariant={variant}>{getEddDisplay()}</EDDText>
    </EDDContainer>
  );
};

export { EDD };
